import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import { useInstallPrompt } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  User, BookmarkCheck, SlidersHorizontal, Info, FileText, Shield,
  HelpCircle, Mail, Download, LogOut, ChevronRight,
} from "lucide-react";

const APP_VERSION = "1.0.0";

export default function Account() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { promptInstall, isIOS } = useInstallPrompt();

  const initials = (user?.name || "?")
    .split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const doInstall = async () => {
    const res = await promptInstall();
    if (res === "ios") toast.info("On iPhone/iPad: tap Share, then ‘Add to Home Screen’.");
    else if (res === "unavailable") toast.info("Your browser doesn’t support install, or BrewMate is already installed.");
  };

  const doLogout = async () => {
    const url = await logout();
    if (url) window.location.href = url;
    else navigate("/login", { replace: true });
  };

  const items = [
    { icon: User, label: "Manage Profile", onClick: () => navigate("/account/profile"), testid: "acct-profile" },
    { icon: BookmarkCheck, label: "Saved Recipes", onClick: () => navigate("/recipes?tab=saved"), testid: "acct-saved" },
    { icon: SlidersHorizontal, label: "Brewing Preferences", onClick: () => navigate("/account/preferences"), testid: "acct-prefs" },
    { icon: Info, label: "About BrewMate", onClick: () => navigate("/about"), testid: "acct-about" },
    { icon: FileText, label: "Terms & Conditions", onClick: () => navigate("/terms"), testid: "acct-terms" },
    { icon: Shield, label: "Privacy Policy", onClick: () => navigate("/privacy"), testid: "acct-privacy" },
    { icon: HelpCircle, label: "Help & Support", onClick: () => navigate("/help"), testid: "acct-help" },
    { icon: Mail, label: "Contact Us", onClick: () => navigate("/contact"), testid: "acct-contact" },
    { icon: Download, label: "Install BrewMate", onClick: doInstall, testid: "acct-install" },
  ];

  return (
    <div className="px-5 pt-8" data-testid="account-page">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#F57C00] text-[#1F2A44] flex items-center justify-center text-xl font-bold" data-testid="account-initials">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="text-lg font-semibold text-[#1F2A44] truncate">{user?.name}</div>
          <div className="text-sm text-[#1F2A44]/65 truncate">{user?.email}</div>
        </div>
      </div>

      <div className="mt-7 space-y-2">
        {items.map(({ icon: Icon, label, onClick, testid }) => (
          <button key={label} onClick={onClick} data-testid={testid}
            className="w-full flex items-center gap-3 rounded-2xl border border-[#F57C00] bg-[#F57C00] p-4 text-[#1F2A44] active:scale-[0.99] transition-transform hover:bg-[#E86F00]">
            <Icon size={20} />
            <span className="flex-1 text-left font-medium">{label}</span>
            <ChevronRight size={18} className="text-[#1F2A44]/60" />
          </button>
        ))}

        <button onClick={doLogout} data-testid="logout-btn"
          className="w-full flex items-center gap-3 rounded-2xl border border-[#F57C00] bg-[#F57C00] p-4 text-[#1F2A44] active:scale-[0.99] transition-transform hover:bg-[#E86F00]">
          <LogOut size={20} />
          <span className="flex-1 text-left font-medium">Log Out</span>
        </button>
      </div>

      <p className="mt-6 text-center text-xs text-[#1F2A44]/55">
        <Wordmark className="text-[#1F2A44]/70" /> v{APP_VERSION} · Powered by Wonderbean Coffee Co.
      </p>
      <div className="h-4" />
    </div>
  );
}
